import React, { SVGProps, useCallback } from "react";
import styled from "styled-components";

import Button from "@ingka/button";
import clipboardCheckmark from "@ingka/ssr-icon/paths/clipboard-checkmark";
import display from "@ingka/ssr-icon/paths/display";
import globe from "@ingka/ssr-icon/paths/globe";
import people from "@ingka/ssr-icon/paths/people";
import dataChart from "@ingka/ssr-icon/paths/data-chart";
import { OverflowCarousel } from "@ingka/carousel";
import { ListViewItem } from "@ingka/list-view";
import { breakpointL } from "@ingka/variables/design-tokens";

import ConfigurationPanel from "./ConfigurationPanel";
import UserPanel from "./UsersPanel";
import UsersRequestedPanel from "./UsersRequestedPanel";
import MarketsPanel from "./MarketsPanel";
import DashboardPanel from "./DashboardPanel";
import MarketDashboardPanel from "./MarketDashboardPanel";
import { HomePageLayoutTabs } from "../../utils/constants";
import useTabs from "../../hooks/useTabs";
import useWindowSize from "../../hooks/useWindowSize";
import { headerAndFooterHeight } from "../../utils/variables";
import useStore, {
	activeApplicationSelector,
	setApplicationUsersSelector,
	applicationUsersSelector,
} from "../../store";
import Schema from "../Schema";
import UiSchemas from "../ui-schemas";
import Schemas from "../Schemas";
import useAuthentication from "../../hooks/useAuthentication";
import { getAppUsersList } from "../../services/users";
import { Counter } from "../Counter/Counter";
import { ButtonDropdown } from "../Dropdown";

const StyledOverflowCarousel = styled(OverflowCarousel)`
	.overflow-carousel__content {
		gap: 0.5rem;
	}
`;

export const FullHeightContainer = styled.div`
	min-height: calc(100vh - ${headerAndFooterHeight});
	padding-bottom: 2rem;
`;

const StyledSectionWrapper = styled.div`
	margin-top: 2rem;

	@media only screen and (max-width: ${breakpointL}) {
		margin-top: 1rem;
	}
`;

const UserRequestedWrapperMenu = styled.div`
	display: flex;
	gap: 0.5rem;
`;

interface HomePageLayoutProps {
	showAdminLayout?: boolean;
}

type SSRIconPath = ((prefix?: string | undefined) => SVGProps<SVGElement>[]) | undefined;

interface NavigationItem {
	id: string;
	label: string;
	tab: HomePageLayoutTabs;
	icon: SSRIconPath;
	visible: boolean;
	menuItems?: {
		id: string;
		label: string;
		tab: HomePageLayoutTabs;
		disabled?: boolean;
	}[];
}

const TAB_COMPONENTS: Record<HomePageLayoutTabs, React.ReactNode> = {
	[HomePageLayoutTabs.Configuration]: <ConfigurationPanel />,
	[HomePageLayoutTabs.Schemas]: <Schemas />,
	[HomePageLayoutTabs.Markets]: <MarketsPanel />,
	[HomePageLayoutTabs.Users]: <UserPanel />,
	[HomePageLayoutTabs.UsersRequested]: <UsersRequestedPanel />,
	[HomePageLayoutTabs.Dashboard]: <DashboardPanel />,
	[HomePageLayoutTabs.MarketDashboard]: <MarketDashboardPanel />,
	[HomePageLayoutTabs.SchemaBuilder]: <Schema />,
	[HomePageLayoutTabs.UiSchemaBuilder]: <UiSchemas />,
	[HomePageLayoutTabs.Requests]: undefined,
};

const HomePageLayout: React.FC<HomePageLayoutProps> = ({ showAdminLayout }) => {
	const { activeTab, setActiveTab, getTabLink } = useTabs();
	const { isMobile, isTablet, isDesktop, isBigDesktop } = useWindowSize();

	const activeApplication = useStore(activeApplicationSelector);
	const applicationUsers = useStore(applicationUsersSelector);
	const setApplicationUsers = useStore(setApplicationUsersSelector);

	const { getToken } = useAuthentication();

	const [openMenu, setOpenMenu] = React.useState<NavigationItem["id"] | null>();

	const DEFAULT_ACTIVE_TAB = HomePageLayoutTabs.Configuration;

	React.useEffect(() => {
		const allTabs = Object.values(HomePageLayoutTabs);

		if (
			!activeTab ||
			(activeTab && !allTabs.includes(activeTab as HomePageLayoutTabs)) ||
			(activeTab === HomePageLayoutTabs.Dashboard && !activeApplication?.showDashboard) ||
			(activeTab === HomePageLayoutTabs.MarketDashboard && !activeApplication?.showMarketDashboard)
		) {
			setActiveTab(DEFAULT_ACTIVE_TAB);
		}
	}, [activeTab, activeApplication, setActiveTab, DEFAULT_ACTIVE_TAB]);

	React.useEffect(() => {
		let mounted = true;

		(async () => {
			if (!activeApplication) {
				setApplicationUsers(null);
				return;
			}

			try {
				const token = await getToken();
				const { data } = await getAppUsersList(token, activeApplication.id);

				if (mounted) {
					setApplicationUsers(data.data);
				}
			} catch {
				if (mounted) {
					setApplicationUsers(null);
				}
			}
		})();

		return () => {
			mounted = false;
			setApplicationUsers(null);
		};
	}, [activeApplication, getToken, setApplicationUsers]);

	const handleTabChange = React.useCallback(
		(e: React.SyntheticEvent, tab: HomePageLayoutTabs) => {
			e.preventDefault();
			setActiveTab(tab);
			setOpenMenu(null);
		},
		[setActiveTab]
	);

	const navItems = React.useMemo<NavigationItem[]>(
		() => [
			{
				id: "configuration",
				label: "Configuration",
				tab: HomePageLayoutTabs.Configuration,
				icon: clipboardCheckmark,
				visible: true,
			},
			{
				id: "schemas",
				label: "Schemas",
				tab: HomePageLayoutTabs.Schemas,
				icon: display,
				visible: Boolean(showAdminLayout),
				menuItems: [
					{ id: "schemaBuilder", label: "Schema Builder", tab: HomePageLayoutTabs.SchemaBuilder },
					{ id: "layoutBuilder", label: "Layout Builder", tab: HomePageLayoutTabs.UiSchemaBuilder },
					{ id: "jsonSchemas", label: "JSON Schemas", tab: HomePageLayoutTabs.Schemas },
				],
			},
			{
				id: "markets",
				label: "Markets",
				tab: HomePageLayoutTabs.Markets,
				icon: globe,
				visible: Boolean(showAdminLayout),
			},
			{
				id: "dashboard",
				label: "Dashboard",
				tab: HomePageLayoutTabs.Dashboard,
				icon: dataChart,
				visible: Boolean(activeApplication?.showDashboard || activeApplication?.showMarketDashboard),

				menuItems: [
					{
						id: "marketDashboard",
						label: "Market dashboard",
						tab: HomePageLayoutTabs.MarketDashboard,
						disabled: !activeApplication?.showMarketDashboard,
					},
					{
						id: "globalDashboard",
						label: "Global dashboard",
						tab: HomePageLayoutTabs.Dashboard,
						disabled: !activeApplication?.showDashboard,
					},
				],
			},
			{
				id: "users",
				label: "Users",
				tab: HomePageLayoutTabs.Users,
				icon: people,
				visible: Boolean(showAdminLayout),
				menuItems: [
					{ id: "usersOnboard", label: "Users onboarded", tab: HomePageLayoutTabs.Users },
					{
						id: "usersRequest",
						label: "Users requests",
						tab: HomePageLayoutTabs.UsersRequested,
					},
				],
			},
		],
		[showAdminLayout, activeApplication]
	);

	const getControlType = useCallback(
		(item: NavigationItem) => {
			if (item.tab === activeTab || item.menuItems?.find((menu) => menu.id === activeTab)) {
				("secondary");
			}
			return "tertiary";
		},
		[activeTab]
	);

	return (
		<FullHeightContainer>
			<nav aria-label="navigation">
				{(isBigDesktop || isDesktop) && (
					<StyledOverflowCarousel>
						{navItems
							.filter((item) => item.visible)
							.map((item) => {
								if (!item.menuItems?.length) {
									return (
										<Button
											key={item.id}
											data-testid={item.id}
											text={item.label}
											ssrIcon={item.icon}
											href={getTabLink(item.tab)}
											size={isMobile || isTablet ? "small" : "medium"}
											type={getControlType(item)}
											onClick={(e: React.SyntheticEvent) => handleTabChange(e, item.tab)}
										/>
									);
								}

								return (
									<ButtonDropdown
										id={`${item.id}`}
										key={item.id}
										text={item.label}
										ssrIcon={item.icon}
										open={openMenu === item.id}
										setMenuOpen={(open: boolean) => setOpenMenu(open ? item.id : null)}
										type={getControlType(item)}
										className={"marginRight: 0.5rem"}
									>
										{item.menuItems.map((menuItem) => (
											<ListViewItem
												data-testid={`${menuItem.id}-item`}
												key={menuItem.id}
												control="link"
												inset
												disabled={menuItem.disabled}
												href={getTabLink(menuItem.tab)}
												title={
													menuItem.id === HomePageLayoutTabs.Users ? (
														<UserRequestedWrapperMenu>
															{menuItem.label}
															{applicationUsers?.requests?.length ? (
																<Counter>{applicationUsers.requests.length}</Counter>
															) : null}
														</UserRequestedWrapperMenu>
													) : (
														menuItem.label
													)
												}
												onClick={(e: React.SyntheticEvent) => handleTabChange(e, menuItem.tab)}
											/>
										))}
									</ButtonDropdown>
								);
							})}
					</StyledOverflowCarousel>
				)}
			</nav>

			<StyledSectionWrapper>
				{TAB_COMPONENTS[activeTab as HomePageLayoutTabs] ?? <ConfigurationPanel />}
			</StyledSectionWrapper>
		</FullHeightContainer>
	);
};

export default HomePageLayout;
